import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'components/ui/alert-dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { createOrganization, deleteOrganization, invalidateOrganizationsCache } from 'services/api/organizations';
import { Building } from 'services/api/buildings';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Building2, Loader2 } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useHomePageData } from '../../hooks/useHomePageData';

type SortKey = 'name' | 'createdAt' | 'buildingCount' | 'memberCount';
type SortDirection = 'asc' | 'desc';

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading: dataLoading, error: dataError, refetch } = useHomePageData(user?.role || null);
  
  // Extract and process data from the cached data
  const organizations = useMemo(() => {
    if (!data?.organizations || !data?.buildings) return [];
    
    return data.organizations.map(org => ({
      ...org,
      buildingCount: data.buildings.filter((b: Building) => b.organizationId === org.id).length,
      memberCount: org.members ? org.members.length : 0,
    }));
  }, [data]);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgAddress, setNewOrgAddress] = useState('');
  const [newOrgContact, setNewOrgContact] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Handle sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      toast.warning("Organization name cannot be empty.");
      return;
    }
    try {
      setIsCreatingOrganization(true);
      const orgData: any = { name: newOrgName };
      
      // Only add optional fields if they have values
      if (newOrgAddress.trim()) {
        orgData.address = newOrgAddress.trim();
      }
      if (newOrgContact.trim()) {
        orgData.contact = newOrgContact.trim();
      }
      
      await createOrganization(orgData);
      // Invalidate and refetch latest orgs dataset
      invalidateOrganizationsCache();
      await refetch();
      toast.success(`Organization "${newOrgName}" created successfully!`);
      setNewOrgName('');
      setNewOrgAddress('');
      setNewOrgContact('');
      setIsDialogOpen(false);
      await refetch(); // Refresh the cached data
    } catch (err) {
      toast.error('Failed to create organization.');
      console.error(err);
    } finally {
      setIsCreatingOrganization(false);
    }
  };

  const handleDeleteOrganization = async (id: string) => {
    try {
      setDeletingId(id);
      await deleteOrganization(id);
      invalidateOrganizationsCache();
      toast.success("Organization deleted successfully.");
      await refetch(); // Refresh the cached data
    } catch (err) {
      toast.error("Failed to delete organization.");
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredOrgs = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = organizations.filter(org => 
      term ? org.name.toLowerCase().includes(term) : true
    );
    
    // Apply sorting
    list.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortKey) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt;
          bValue = b.createdAt;
          break;
        case 'buildingCount':
          aValue = a.buildingCount;
          bValue = b.buildingCount;
          break;
        case 'memberCount':
          aValue = a.memberCount;
          bValue = b.memberCount;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return list;
  }, [organizations, search, sortKey, sortDirection]);

  const totalPages = Math.ceil(filteredOrgs.length / itemsPerPage);
  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp format {_seconds, _nanoseconds}
      if (timestamp._seconds && typeof timestamp._seconds === 'number') {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle Firestore Timestamp object
      else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle regular Date object or timestamp number
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  if (dataLoading) return (
    <div className="space-y-6 pt-2">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );
  
  if (dataError) return (
    <div className="text-center text-red-600 p-4">
      <p>{dataError}</p>
      <Button onClick={refetch} className="mt-4">Retry</Button>
    </div>
  );

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
                  <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-r from-emerald-100 to-green-100 rounded-lg">
                <Building2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Organizations</h1>
            </div>
            <p className="text-emerald-400 font-medium ml-14">Manage all organizations in the system.</p>
          </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">Create Organization</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create a new organization</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">Name</Label>
                <Input 
                  id="name" 
                  value={newOrgName} 
                  onChange={(e) => setNewOrgName(e.target.value)} 
                  disabled={isCreatingOrganization}
                  className="col-span-3" 
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="address" className="text-right">Address</Label>
                <Input 
                  id="address" 
                  value={newOrgAddress} 
                  onChange={(e) => setNewOrgAddress(e.target.value)} 
                  disabled={isCreatingOrganization}
                  className="col-span-3" 
                  placeholder="Optional" 
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact" className="text-right">Contact</Label>
                <Input 
                  id="contact" 
                  value={newOrgContact} 
                  onChange={(e) => setNewOrgContact(e.target.value)} 
                  disabled={isCreatingOrganization}
                  className="col-span-3" 
                  placeholder="Phone or email (optional)" 
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                disabled={isCreatingOrganization}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                onClick={handleCreateOrganization}
                disabled={isCreatingOrganization}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isCreatingOrganization ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Create Organization
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="bg-gradient-to-r from-white to-gray-50 border-gray-200">
        <CardHeader>
          <div className="flex items-center gap-4">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations..."
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-emerald-100 to-green-100 border-emerald-200">
                <TableHead className="text-emerald-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('name')}
                    className="h-auto p-0 font-semibold hover:bg-emerald-200 text-emerald-900"
                  >
                    Name {getSortIcon('name')}
                  </Button>
                </TableHead>
                <TableHead className="text-emerald-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('createdAt')}
                    className="h-auto p-0 font-semibold hover:bg-emerald-200 text-emerald-900"
                  >
                    Created Date {getSortIcon('createdAt')}
                  </Button>
                </TableHead>
                <TableHead className="text-emerald-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('buildingCount')}
                    className="h-auto p-0 font-semibold hover:bg-emerald-200 text-emerald-900"
                  >
                    Buildings {getSortIcon('buildingCount')}
                  </Button>
                </TableHead>
                <TableHead className="text-emerald-900 font-semibold">
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('memberCount')}
                    className="h-auto p-0 font-semibold hover:bg-emerald-200 text-emerald-900"
                  >
                    Members {getSortIcon('memberCount')}
                  </Button>
                </TableHead>
                <TableHead className="text-emerald-900 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedOrgs.length > 0 ? (
                paginatedOrgs.map((org) => (
                  <TableRow key={org.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(org.createdAt)}</TableCell>
                    <TableCell className="text-muted-foreground">{org.buildingCount}</TableCell>
                    <TableCell className="text-muted-foreground">{org.memberCount}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/organizations/${org.id}`)}
                        >
                          View Details
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" disabled={deletingId === org.id}>
                              {deletingId === org.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                'Delete'
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the
                                organization and its associated data.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={deletingId === org.id}>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteOrganization(org.id)} className="bg-red-600 hover:bg-red-700" disabled={deletingId === org.id}>
                                {deletingId === org.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Deleting...
                                  </>
                                ) : (
                                  'Continue'
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    No organizations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Precedente
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Successivo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationsPage; 